// App settings: one synced `settings` row per key, value stored as JSON (SPEC.md §4.3).
import { useDbQuery } from '../db/hooks';
import type { DbClient } from '../db/client';

/** The last reminders file made (SPEC.md §8), to tell when it no longer matches meds or schedule. */
export interface RemindersExport {
  fingerprint: string;
  /** Local date it was made, 'yyyy-MM-dd'. */
  since: string;
  options: { sessions: boolean; medications: boolean; vaccinations: boolean; sessionAlarmMinutes: number };
  at: string;
}

export interface AppSettings {
  currency: string;
  fluid_limit_ml: number | null;
  week_starts_on: 'mon' | 'sun';
  onboarding_done_at: string | null;
  reminders_export: RemindersExport | null;
  /** Doctor report choices (SPEC.md §4.3 settings.report_defaults). */
  report_defaults: { days: number; sections: string[] } | null;
}

export const DEFAULT_SETTINGS: AppSettings = {
  currency: 'USD',
  fluid_limit_ml: null,
  week_starts_on: 'mon',
  onboarding_done_at: null,
  reminders_export: null,
  report_defaults: null
};

type SettingKey = keyof AppSettings;

export function useSettings() {
  const query = useDbQuery<{ id: string; value: string | null }>('SELECT id, value FROM settings WHERE deleted_at IS NULL', [], ['settings']);
  const settings: AppSettings = { ...DEFAULT_SETTINGS };
  for (const row of query.data ?? []) {
    if (!(row.id in DEFAULT_SETTINGS) || row.value == null) continue;
    try {
      (settings as unknown as Record<string, unknown>)[row.id] = JSON.parse(row.value);
    } catch {
      // Ignore a malformed value and keep the default.
    }
  }
  return { settings, isLoading: query.isLoading };
}

export function saveSetting<K extends SettingKey>(db: DbClient, key: K, value: AppSettings[K]) {
  return db.upsert('settings', key, { value: JSON.stringify(value) });
}
